import { ArrowUpRight, Satellite, Globe2, ShieldCheck, Play, Briefcase, Compass } from "lucide-react";
import { useRef } from "react";
import { Link } from "react-router-dom";
import { HERO_IMAGES } from "@/data/products";
import { Marquee } from "@/components/Marquee";
import { ScrollReveal } from "@/components/ScrollReveal";
import { SEO } from "@/components/SEO";
import { EnquiryForm } from "@/components/EnquiryForm";
import { UntamedCarousel } from "@/components/UntamedCarousel";

const UNTAMED_GALLERY_PHOTOS = [
  { src: "/api/files/xpeditionz/expeditions/untamed/fa5ea65b-1694-498d-b56e-3e1fdec59272.webp", alt: "Bush Cabin Hilux set up at dusk in South Island beech forest, campfire and camp chairs out" },
  { src: "/api/files/xpeditionz/expeditions/untamed/88547b88-1f1d-4338-a2d6-bf5e7710f40a.webp", alt: "Bush Cabin Hilux camped riverside at sunset, rooftop tent up, snow-capped Southern Alps behind" },
  { src: "/api/files/xpeditionz/expeditions/untamed/c23a6b64-8d1d-4750-8a17-93800119eab9.webp", alt: "XpeditioNZ demonstrator vehicle beside a braided South Island river, scree mountain behind", position: "center 72%" },
  { src: "/api/files/xpeditionz/expeditions/untamed/85e2b8e5-342e-4ab7-879a-db989b767175.webp", alt: "XpeditioNZ demonstrator vehicle on a high-country ridge track, Southern Alps in the distance", position: "center 72%" },
  { src: "/api/files/xpeditionz/expeditions/untamed/515e4169-acf1-4ea1-a7e4-71e70aa7d0a0.webp", alt: "XpeditioNZ demonstrator vehicle on a rocky high-country trail, tussock hills and winding track behind" },
  { src: "/api/files/xpeditionz/expeditions/untamed/2742f380-1e0e-408a-8ea9-247cca025bc5.webp", alt: "Bush Cabin Hilux parked at a South Island glacial river crossing, snow-capped mountain behind" },
  { src: "/api/files/xpeditionz/expeditions/untamed/d2ccc03d-b70a-45be-a1d0-6f91c75610cc.webp", alt: "Bush Cabin Hilux camped in the snow beside a campfire grill, Southern Alps behind" },
  { src: "/api/files/xpeditionz/expeditions/untamed/8770783c-7288-4c9a-a9bc-b4de10e3cbb5.webp", alt: "Bush Cabin Hilux set up at dusk on a sandy South Island beach, rooftop tent open", fit: "contain" },
];

const EXPEDITIONS_MARQUEE = [
  "SOUTH ISLAND WILDERNESS",
  "STARLINK EQUIPPED",
  "SOUTH AFRICAN BUILD CALIBRE",
  "GLOBAL EXPEDITION EXPERIENCE",
  "BRAND & PRODUCTION READY",
  "TRUE OFF-GRID CAPABILITY",
];

const AUDIENCE = [
  { icon: Briefcase, t: "Brands & Companies", d: "Genuinely remote, scenic, off-grid locations for content or campaigns — not a studio backdrop." },
  { icon: Compass, t: "Individuals & Couples", d: "A completely bespoke, tailored lifestyle experience — enquire to find out what's possible." },
];

export default function ExpeditionsPage() {
  const videoRef = useRef(null);

  const handleTimeUpdate = (e) => {
    if (e.currentTarget.currentTime >= 58) {
      e.currentTarget.currentTime = 0;
    }
  };

  return (
    <div data-testid="expeditions-page">
      <SEO
        path="/expeditions"
        title="XpeditioNZ Experience"
        description="Stunning South Island wilderness, and a fleet of uniquely equipped, Starlink-connected expedition vehicles built to a calibre rarely seen in NZ. Enquire to find out more."
        image={HERO_IMAGES.bushCabinOpened}
      />

      {/* HERO */}
      <section className="relative min-h-[100svh] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <video
            ref={videoRef}
            data-testid="expeditions-hero-video"
            autoPlay
            muted
            playsInline
            preload="auto"
            poster={HERO_IMAGES.xseries}
            onTimeUpdate={handleTimeUpdate}
            className="w-full h-full object-cover"
            style={{ objectPosition: "center 35%" }}
          >
            <source src={HERO_IMAGES.xseriesVideo1080} type="video/mp4" media="(min-width: 1280px)" />
            <source src={HERO_IMAGES.xseriesVideo720} type="video/mp4" />
          </video>
          <div className="absolute inset-0 bg-gradient-to-t from-[var(--bg-main)] from-8% via-black/20 via-40% to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/10 to-transparent md:via-transparent" />
        </div>
        <div className="relative z-10 container-x pb-20 pt-32 w-full">
          <p className="overline mb-6">— XpeditioNZ Experience</p>
          <h1 className="font-display text-[11vw] md:text-[8vw] lg:text-[6.5vw] leading-[0.88] text-white hero-text-shadow max-w-[18ch]">
            SOUTH ISLAND WILDERNESS.<br />
            <span className="text-[var(--brand-primary)]">VEHICLES BUILT TO REACH IT.</span>
          </h1>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-8 items-end">
            <p className="text-base md:text-lg font-medium md:font-normal text-white/95 md:text-white/85 leading-relaxed max-w-xl [text-shadow:0_1px_3px_rgba(0,0,0,0.9),0_2px_20px_rgba(0,0,0,0.7)] md:[text-shadow:none]">
              Built for those seeking a uniquely tailored lifestyle experience in genuinely remote, off-grid scenic locations — with capability for brands and companies too. Our demonstrator vehicles are built to a calibre rarely seen in New Zealand, backed by our team's extensive knowledge of the South Island's natural wilderness and the routes well off the beaten track. Enquire to find out what's possible.
            </p>
            <div className="flex flex-wrap gap-3 md:justify-end">
              <a href="#enquire" data-testid="expeditions-hero-cta-enquire" className="btn-primary">
                Enquire Now <ArrowUpRight size={14} />
              </a>
              <a href="#vehicles" data-testid="expeditions-hero-cta-vehicles" className="btn-secondary">
                See Our Fleet
              </a>
            </div>
          </div>
        </div>
      </section>

      <Marquee items={EXPEDITIONS_MARQUEE} />

      {/* GALLERY */}
      <section className="pt-16 md:pt-20 pb-16 md:pb-20">
        <div className="container-x mb-10">
          <p className="overline mb-4">— The Vehicles In The Field</p>
          <h2 className="font-display text-4xl md:text-5xl text-white leading-[0.95]">
            OUR VEHICLES <span className="text-[var(--brand-primary)]">IN THE FIELD.</span>
          </h2>
        </div>
        <UntamedCarousel images={UNTAMED_GALLERY_PHOTOS} />
      </section>

      {/* MISSION / SPLIT NARRATIVE */}
      <section className="container-x py-16 md:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
          <div className="lg:col-span-6 order-2 lg:order-1">
            <p className="overline mb-6">— What We Do</p>
            <h2 className="font-display text-4xl md:text-5xl leading-[0.95] text-white mb-8">
              THE VEHICLE IS<br />USUALLY THE<br /><span className="text-[var(--brand-primary)]">LIMITING FACTOR.</span>
            </h2>
            <div className="space-y-5 text-[var(--text-secondary)] text-lg leading-relaxed">
              <p>
                The right vehicle, comfortable and self-sufficient for days at a time, is usually the missing piece.
              </p>
              <p>
                That's where our Xpeditionz fleet comes in — uniquely equipped, and built well beyond anything typically available to hire or borrow in NZ.
              </p>
              <p>
                Backed by a team that's actually planned and run cross-continent 4WD expeditions worldwide. For individuals or couples, it's a completely bespoke, tailored lifestyle experience. Enquire and we'll talk through what's possible.
              </p>
            </div>
          </div>
          <div className="lg:col-span-6 order-1 lg:order-2">
            <div className="relative aspect-[4/3] overflow-hidden border border-[var(--border-default)]">
              <img src="https://www.xpeditionz.co.nz/api/files/xpeditionz/bushcabin/6a56db5ff843dd19ef0df337/b74ca77d-eb94-49f1-b9fb-263f79380ac5.jpg" alt="Demonstrator vehicle set up in remote South Island terrain" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* THE VEHICLES */}
      <section id="vehicles" className="container-x pb-16 md:pb-20 scroll-mt-24">
        <div className="mb-12 max-w-3xl">
          <p className="overline mb-4">— Our Fleet</p>
          <h2 className="font-display text-4xl md:text-6xl text-white leading-[0.95]">
            BUILT TO A CALIBRE<br />RARELY SEEN<br /><span className="text-[var(--brand-primary)]">IN NEW ZEALAND.</span>
          </h2>
          <p className="text-[var(--text-secondary)] mt-6 text-lg leading-relaxed">
            Both demonstrator vehicles are upgraded exclusively with top-tier world brands, imported specifically from South Africa for these builds — a calibre of equipment rarely seen on the New Zealand market. Full equipment breakdown below, and you'll see them out in the field throughout this page.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div data-testid="expeditions-vehicle-bushcabin" className="border border-[var(--border-default)] bg-[var(--bg-secondary)] overflow-hidden group">
            <div className="aspect-[4/3] overflow-hidden relative">
              <img
                src={HERO_IMAGES.bushcabin}
                alt="Bush Cabin demonstrator — insulated live-in ute camper"
                className="absolute inset-0 w-full h-full object-cover scale-125 -translate-x-[10%] transition-all duration-500 ease-out group-hover:opacity-0 group-hover:scale-[1.35] group-hover:-translate-x-[10%]"
              />
              <img
                src="https://www.xpeditionz.co.nz/api/files/xpeditionz/bushcabin/6a56db5ff843dd19ef0df337/c1a87830-5695-492a-853c-897cc3b44571.jpg"
                alt="Bush Cabin demonstrator — alternate view"
                data-testid="expeditions-vehicle-bushcabin-hover-image"
                className="absolute inset-0 w-full h-full object-cover opacity-0 scale-125 transition-all duration-500 ease-out group-hover:opacity-100"
                style={{ objectPosition: "65% center" }}
              />
            </div>
            <div className="p-6 md:p-8">
              <p className="font-mono text-xs tracking-[0.25em] uppercase text-[var(--brand-primary)]">Demonstrator 01</p>
              <h3 className="font-display text-2xl md:text-3xl text-white mt-2">BUSH CABIN HILUX</h3>
              <p className="text-[var(--text-secondary)] mt-3 leading-relaxed">
                A fully insulated, live-in ute camper — genuine shelter and comfort at the end of the track, whatever the weather does.
              </p>
              <ul className="mt-4 space-y-1.5 text-sm text-[var(--text-secondary)]">
                <li className="flex items-start gap-2">
                  <span className="mt-1.5 w-1 h-1 bg-[var(--brand-primary)] shrink-0" />
                  Fully insulated and heated
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-1.5 w-1 h-1 bg-[var(--brand-primary)] shrink-0" />
                  Starlink available
                </li>
              </ul>
              <Link to="/bush-cabin" data-testid="expeditions-vehicle-bushcabin-link" className="inline-flex items-center gap-1.5 mt-5 text-sm font-mono uppercase tracking-[0.15em] text-white group-hover:text-[var(--brand-primary)] transition-colors">
                About This Build <ArrowUpRight size={14} />
              </Link>
            </div>
          </div>

          <div data-testid="expeditions-vehicle-xseries" className="border border-[var(--border-default)] bg-[var(--bg-secondary)] overflow-hidden group">
            <div className="aspect-[4/3] overflow-hidden relative">
              <img
                src={HERO_IMAGES.xseriesDuo}
                alt="X-Series tray demonstrator — expedition-grade service body"
                className="absolute inset-0 w-full h-full object-cover transition-all duration-500 ease-out group-hover:opacity-0 group-hover:scale-105"
              />
              <img
                src="/products/xseries/731077_c83f0402209a4e5caece6cf599b395ed~mv2.jpg"
                alt="X-Series tray demonstrator — alternate view"
                data-testid="expeditions-vehicle-xseries-hover-image"
                className="absolute inset-0 w-full h-full object-cover opacity-0 transition-all duration-500 ease-out group-hover:opacity-100"
              />
            </div>
            <div className="p-6 md:p-8">
              <p className="font-mono text-xs tracking-[0.25em] uppercase text-[var(--brand-primary)]">Demonstrator 02</p>
              <h3 className="font-display text-2xl md:text-3xl text-white mt-2">X-SERIES TRAY HILUX</h3>
              <p className="text-[var(--text-secondary)] mt-3 leading-relaxed">
                An expedition-grade service body with 75L onboard water and a 500kg dynamic load rating — built for genuinely remote, self-sufficient travel.
              </p>
              <ul className="mt-4 space-y-1.5 text-sm text-[var(--text-secondary)]">
                {[
                  "Koni RAID suspension",
                  "ONCA bar work, front & rear, with winch",
                  "Heavy-duty wheels and tyres",
                  "South African commercial-grade roof tent and awning",
                  "Insulated and heated tent",
                  "Kitchen side: induction cooking, fridge and a proper espresso machine",
                  "Starlink available",
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="mt-1.5 w-1 h-1 bg-[var(--brand-primary)] shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <Link to="/x-series-tray" data-testid="expeditions-vehicle-xseries-link" className="inline-flex items-center gap-1.5 mt-5 text-sm font-mono uppercase tracking-[0.15em] text-white group-hover:text-[var(--brand-primary)] transition-colors">
                About This Build <ArrowUpRight size={14} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* SEE IT IN ACTION */}
      <section id="in-action" className="container-x pb-16 md:pb-20 scroll-mt-24">
        <div className="mb-10 max-w-2xl">
          <p className="overline mb-4">— See It In Action</p>
          <h2 className="font-display text-4xl md:text-5xl text-white leading-[0.95]">
            DON'T JUST TAKE<br /><span className="text-[var(--brand-primary)]">OUR WORD FOR IT.</span>
          </h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {[
            {
              url: "https://www.youtube.com/watch?v=58FAif6dNQI",
              thumb: "https://i.ytimg.com/vi/58FAif6dNQI/maxresdefault.jpg",
              label: "X-Series Tray Walkaround",
              desc: "A full walkthrough of the build, fit-out and equipment.",
            },
            {
              url: "https://www.youtube.com/watch?v=pHYTHaYRiZI",
              thumb: "https://i.ytimg.com/vi/pHYTHaYRiZI/maxresdefault.jpg",
              label: "Full Touring Build",
              desc: "The X-Series in the field, South Island backcountry.",
            },
          ].map((v, i) => (
            <a
              key={i}
              href={v.url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`expeditions-video-${i}`}
              className="group relative aspect-video overflow-hidden border border-[var(--border-default)] block"
            >
              <img src={v.thumb} alt={v.label} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
              <div className="absolute inset-0 bg-black/45 group-hover:bg-black/25 transition-colors" />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-16 h-16 rounded-full bg-[var(--brand-primary)] flex items-center justify-center group-hover:scale-110 transition-transform">
                  <Play size={22} className="text-black ml-1" fill="currentColor" />
                </div>
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-5 bg-gradient-to-t from-black/90 to-transparent">
                <p className="text-white font-display text-lg">{v.label}</p>
                <p className="text-white/70 text-sm mt-1">{v.desc}</p>
              </div>
            </a>
          ))}
        </div>
      </section>

      {/* FULL-BLEED — scroll reveal */}
      <ScrollReveal
        src="/api/files/xpeditionz/rooftent/6a5550fe81ab847e44f89d15/a0267ac7-febf-44cd-8020-f4c5237616ee.jpg"
        alt="South Island wilderness backcountry"
        className="h-[65vh] md:h-[95vh]"
        objectPosition="center 60%"
        overlay={<div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 via-25% to-transparent to-55%" />}
      >
        <div className="relative z-10 h-full flex items-end">
          <div className="container-x pb-16">
            <p className="overline mb-4">— Where The Track Ends</p>
            <h2 className="font-display text-4xl md:text-6xl text-white leading-[0.95] max-w-3xl hero-text-shadow">
              SOUTH ISLAND WILDERNESS,<br />
              <span className="text-[var(--brand-primary)]">WITHOUT THE COMPROMISE.</span>
            </h2>
          </div>
        </div>
      </ScrollReveal>

      {/* STARLINK */}
      <section className="container-x py-16 md:py-20">
        <div className="border border-[var(--brand-primary)]/30 bg-[var(--bg-secondary)] p-8 md:p-14">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-8">
              <Satellite size={32} className="text-[var(--brand-primary)] mb-6" strokeWidth={1.4} />
              <p className="overline mb-4">— Real Work, Real Remote</p>
              <h2 className="font-display text-4xl md:text-5xl text-white leading-[0.95] mb-6">
                STAY CONNECTED.<br /><span className="text-[var(--brand-primary)]">EVEN OUT HERE.</span>
              </h2>
              <p className="text-[var(--text-secondary)] text-lg leading-relaxed max-w-2xl">
                Both demonstrator vehicles are Starlink-equipped — meaning genuine, reliable internet from locations with zero cell coverage. Upload footage, run a video call, or simply keep working, in comfort, from a location most people will never even reach.
              </p>
            </div>
            <div className="lg:col-span-4 flex flex-col gap-4">
              <div className="flex items-start gap-3 border border-[var(--border-default)] bg-[var(--bg-main)] p-5">
                <Globe2 size={20} className="text-[var(--brand-primary)] mt-0.5 shrink-0" strokeWidth={1.6} />
                <p className="text-sm text-white leading-relaxed">Reliable connectivity, completely off the grid.</p>
              </div>
              <div className="flex items-start gap-3 border border-[var(--border-default)] bg-[var(--bg-main)] p-5">
                <ShieldCheck size={20} className="text-[var(--brand-primary)] mt-0.5 shrink-0" strokeWidth={1.6} />
                <p className="text-sm text-white leading-relaxed">Warm, dry, powered workspace inside the Bush Cabin.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHO IT'S FOR */}
      <section className="container-x pb-16 md:pb-20">
        <div className="mb-12">
          <p className="overline mb-4">— Who This Is For</p>
          <h2 className="font-display text-4xl md:text-6xl text-white leading-[0.95] max-w-3xl">
            NOT JUST FOR BRANDS.<br />OPEN TO ANYONE.
          </h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-[var(--border-default)] border border-[var(--border-default)]">
          {AUDIENCE.map((a, i) => {
            const Icon = a.icon;
            return (
              <div key={i} data-testid={`expeditions-audience-${i}`} className="bg-[var(--bg-main)] p-8 hover:bg-[var(--bg-secondary)] transition-colors">
                <Icon size={26} className="text-[var(--brand-primary)] mb-6" strokeWidth={1.3} />
                <h3 className="font-display text-xl text-white tracking-wide leading-tight">{a.t}</h3>
                <p className="text-[var(--text-secondary)] text-sm mt-3 leading-relaxed">{a.d}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* ENQUIRE */}
      <section id="enquire" className="container-x pb-16 md:pb-20 scroll-mt-24">
        <div className="border border-[var(--border-default)] bg-[var(--bg-secondary)] p-6 md:p-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <p className="overline mb-4">— Enquire</p>
              <h2 className="font-display text-3xl md:text-4xl text-white leading-tight mb-6">
                NO OFF-THE-SHELF<br />PACKAGES HERE.
              </h2>
              <p className="text-[var(--text-secondary)] leading-relaxed">
                Scoped around your brief, timeline and location — whether you're a brand after remote content, or after a uniquely tailored personal experience. Tell us what you're after and we'll come back with what's possible.
              </p>
            </div>
            <div className="lg:col-span-7">
              <EnquiryForm idPrefix="expeditions-enquiry" defaultProduct="XpeditioNZ Experience" />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
